import { Grid } from '@material-ui/core';
import React, { useState } from 'react';
import useStyles from './Graph.style';
import { costFormatter2 } from '../../../utils/config';
import { currencies } from '../../../utils/constants';
import { echartsColorPalette } from '../../HealthMonitoring/config';
import ChartCard from './ChartCard';

interface GraphContainerProps {
  dummyAccountData: Record<string, any>;
  dummyTypeData: Record<string, any>;
  isSubmitting: boolean;
}

const GraphContainer: React.FC<GraphContainerProps> = ({
  dummyAccountData,
  dummyTypeData,
  isSubmitting,
}) => {
    const classes = useStyles();
    const [chartInstance, setChartInstance] = useState<echarts.ECharts | null>(null);
    
    // Handle chart initialization
    const handleChartInit = (chart: echarts.ECharts | null) => {
        setChartInstance(chart);
    };

    const generateColors = (count: number) => echartsColorPalette.slice(0, count);
    const getChartOptions = (data: Record<string, any> | undefined): echarts.EChartsOption => {
        if (!data || Object.keys(data).length === 0) return {};

        const legend = Object.keys(data);
        const dates = Object.keys(data[legend[0]]).filter((key) => key !== 'totalCost');
        const colors = generateColors(legend.length);

        const series = legend.map((service: any, index: number) => ({
            name: service,
            type: 'bar' as const,
            stack: 'total',
            data: dates.map((date: any) => data[service][date]?.cost || 0),
            itemStyle: {
            color: colors[index],
            },
        }));

        return {
            tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'cross' },
            confine: false,
            enterable: true,
            formatter: (params: any) => {
                const header = `<div class="echarts-tooltip-title">${params[0].axisValue}</div>`;
                const items = params
                .map((param: any) => {
                    const colorSpan = `<span class="echarts-tooltip-color" style="background-color: ${param.color}"></span>`;
                    return `${colorSpan} <span class="echarts-tooltip-name">${param.seriesName} :</span> <span class="echarts-tooltip-value">${costFormatter2(currencies.USD.shortHand, param.value)}</span>`;
                })
                .join('<br/>');
                return `<div class="echarts-tooltip">${header}${items}</div>`;
            },
            },
            legend: { show: false },
            grid: {
                left: '1%',
                right: '2%',
                bottom: '4%',
                top: '5%',
                containLabel: true,
            },
            dataZoom: [{ type: 'inside', show: true }],
            xAxis: { type: 'category', data: dates },
            yAxis: { type: 'value', axisLabel: { formatter: '${value}' } },
            series: series,
        };
    };
        
    return (
        <div className="graph-container">
            <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={6} lg={6}>
                    <ChartCard
                        title="Data Transfer Costs By Account(s)"
                        data={dummyAccountData}
                        isSubmitting={isSubmitting}
                        generateColors={generateColors}
                        getChartOptions={getChartOptions}
                        onChartInit={handleChartInit}
                        chartInstance={chartInstance}
                        classes={classes}
                        legendHeading="Account(s)"
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6}>
                    <ChartCard
                        title="Data Transfer Costs Per Type"
                        data={dummyTypeData}
                        isSubmitting={isSubmitting}
                        generateColors={generateColors}
                        getChartOptions={getChartOptions}
                        onChartInit={handleChartInit}
                        chartInstance={chartInstance}
                        classes={classes}
                        legendHeading="Usage Types"
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default GraphContainer;